import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';
import { loadReCaptcha } from 'react-recaptcha-v3';

import { withMainControls } from '@eb/eds-structure';

import { TYPE_ERROR } from '@eb/eds-notification';
import { AlertChunky } from '@eb/eds-iconography';
import { LogoEBrand } from '@eb/eds-iconography';
import { Button } from '@eb/eds-button';

import PageHeader from '../../components/PageHeader';
import MagicLinkAccountSection from '../../components/MagicLinkAccountSection';
import LinkAccountsLoginForm from '../../components/newForms/LinkAccountsLoginForm';
import AuthenticationStructure from '../../components/AuthenticationStructure';
import LoginSplitContainer from '../../components/LoginSplitContainer';

/**
 * We currently do not have a test nor a way to access this page.
 * It seems that the redirection occurs after an unknown error
 * from the social provider but using data like the email and
 * checking the user in the database as existent.
 */
/**
 * List of urls that will lead to this page:
 * * BASE_URL/LINK_ACCOUNTS_LOG_IN_PATH
 */
class LinkAccountsLoginPage extends React.PureComponent {
    static propTypes = {
        onLinkAccountsOnLogin: PropTypes.func,
        onForceStld: PropTypes.func,
        email: PropTypes.string,
        handleForgotPassword: PropTypes.func,
        handleClickViewPassword: PropTypes.func,
        moveBack: PropTypes.func,
        clearAuthNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        linkedAccountSuccessUrl: PropTypes.string,
    };

    componentDidMount() {
        if (!this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification, onLinkAccountsOnLogin } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onLinkAccountsOnLogin(formData);
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    render() {
        const {
            handleForgotPassword,
            handleClickViewPassword,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            showCaptcha,
            email,
            moveBack,
            linkedAccountSuccessUrl,
            disableRecaptcha3,
            onForceStld,
            clearAuthNotification,
            socialData: { firstName, userPicture, sourceProvider } = {},
        } = this.props;

        let pageHeaderProps = {
            headerText: gettext('Welcome back'),
            iconType: <LogoEBrand />,
            iconColor: 'white',
            haloColor: 'ui-orange',
        };
        const buttonCTA = gettext('Link your accounts');
        const rightSection = (
            <>
                <div className="eds-text-bl eds-l-pad-bot-4">
                    {gettext(
                        'Or link your accounts with your Eventbrite account:',
                    )}
                </div>

                <LinkAccountsLoginForm
                    buttonCTA={buttonCTA}
                    onSubmit={this._handleSubmit}
                    onForceStld={onForceStld}
                    clearAuthNotification={clearAuthNotification}
                    handleEmailEdit={moveBack}
                    handleForgotPassword={handleForgotPassword}
                    handleClickViewPassword={handleClickViewPassword}
                    recaptcha2PublicKey={recaptcha2PublicKey}
                    recaptcha3PublicKey={recaptcha3PublicKey}
                    disableRecaptcha3={disableRecaptcha3}
                    showCaptcha={showCaptcha}
                    email={email}
                    showNotification={this._handleShowNotification}
                    clearNotification={this._handleClearNotification}
                    linkedAccountSuccessUrl={linkedAccountSuccessUrl}
                    sourceProvider={sourceProvider}
                />

                <div className="eds-text-color--ui-800 eds-align--center eds-l-pad-top-10 eds-l-pad-bot-6">
                    <span className="eds-l-pad-right-1">
                        {gettext('This is not your account?')}
                    </span>

                    <Button
                        onClick={moveBack}
                        type="button"
                        style="anchor"
                        data-automation="go-to-signup"
                    >
                        {gettext('Use another account')}
                    </Button>
                </div>
            </>
        );

        const onLinkAccount = () => {
            return this.props.handleSendMailLinkAccount();
        };

        const leftSection = (
            <>
                <MagicLinkAccountSection
                    email={email}
                    onLinkAccount={onLinkAccount}
                />
            </>
        );

        if (firstName) {
            pageHeaderProps = {
                headerText: gettext('Welcome back, %(firstName)s!', {
                    firstName,
                }),
                iconType: <LogoEBrand />,
                iconColor: 'white',
                haloColor: 'ui-orange',
                picture: userPicture,
            };
        }

        return (
            <AuthenticationStructure>
                <>
                    <PageHeader {...pageHeaderProps} />
                    <LoginSplitContainer
                        leftSection={leftSection}
                        rightSection={rightSection}
                    />
                </>
            </AuthenticationStructure>
        );
    }
}

export default withMainControls(LinkAccountsLoginPage);
